/**
 * Tutorial parser for markdown files with frontmatter
 */

export interface TutorialMetadata {
  title: string;
  description?: string;
  level?: string;
  duration?: string;
  prerequisites?: string[];
  tags?: string[];
  category?: string;
  mocks?: MockData[];
}

export interface MockData {
  id: string;
  code?: string;
  output?: string;
  error?: string;
  executionTime?: number;
  status: "success" | "error";
}

export interface ParsedTutorial {
  metadata: TutorialMetadata;
  content: string;
  slug: string;
}

/**
 * Parse frontmatter from markdown content
 */
function parseFrontmatter(content: string): {
  frontmatter: Record<string, any>;
  body: string;
} {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);

  if (!match) {
    return { frontmatter: {}, body: content };
  }

  const frontmatterText = match[1] || "";
  const body = match[2] || content;

  // Simple YAML-like parser (basic key-value pairs)
  const frontmatter: Record<string, any> = {};
  const lines = frontmatterText.split("\n");

  let currentKey: string | null = null;
  let currentValue: any = null;
  let inArray = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) continue;

    // Handle array items (starting with -)
    if (trimmed.startsWith("- ")) {
      const arrayItem = trimmed.substring(2).trim();
      if (currentKey && inArray) {
        if (!Array.isArray(frontmatter[currentKey])) {
          frontmatter[currentKey] = [];
        }
        // Remove quotes if present
        const cleanValue = arrayItem.replace(/^["']|["']$/g, "");
        frontmatter[currentKey].push(cleanValue);
      }
      continue;
    }

    // Handle key-value pairs
    const colonIndex = trimmed.indexOf(":");
    if (colonIndex > 0) {
      // Save previous key-value if exists
      if (currentKey && currentValue !== null) {
        frontmatter[currentKey] = currentValue;
      }

      currentKey = trimmed.substring(0, colonIndex).trim();
      let value = trimmed.substring(colonIndex + 1).trim();

      // Check if value is an array
      if (value.startsWith("[")) {
        inArray = true;
        // Parse simple array like [item1, item2]
        const arrayContent = value.slice(1, -1);
        if (arrayContent.trim()) {
          frontmatter[currentKey] = arrayContent
            .split(",")
            .map((item) => item.trim().replace(/^["']|["']$/g, ""));
        } else {
          frontmatter[currentKey] = [];
        }
        currentValue = frontmatter[currentKey];
        continue;
      }

      // Check if value is an object (mocks array)
      if (value === "" || value === "|") {
        // Multi-line value or object
        currentValue = null;
        inArray = false;
        continue;
      }

      // Simple value
      value = value.replace(/^["']|["']$/g, "");
      currentValue = value;
      inArray = false;
      frontmatter[currentKey] = currentValue;
    } else if (currentKey && trimmed) {
      // Continuation of previous value (multi-line)
      if (currentValue === null) {
        currentValue = trimmed;
      } else {
        currentValue += `\n${trimmed}`;
      }
    }
  }

  // Save last key-value
  if (currentKey && currentValue !== null) {
    frontmatter[currentKey] = currentValue;
  }

  return { frontmatter, body };
}

/**
 * Parse a tutorial markdown file
 */
export function parseTutorial(content: string, slug: string): ParsedTutorial {
  const { frontmatter, body } = parseFrontmatter(content);

  const metadata: TutorialMetadata = {
    title: frontmatter.title || "Untitled Tutorial",
    description: frontmatter.description || "",
    level: frontmatter.level || frontmatter.difficulty || "beginner",
    duration: frontmatter.duration || "",
    prerequisites: frontmatter.prerequisites || [],
    tags: frontmatter.tags || [],
    category: frontmatter.category || "",
    mocks: frontmatter.mocks || [],
  };

  return {
    metadata,
    content: body,
    slug,
  };
}

/**
 * Extract steps from markdown content (headings)
 * IDs match the headingToId function in TutorialContent
 */
export function extractSteps(content: string): Array<{
  id: string;
  title: string;
  level: number;
}> {
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  const steps: Array<{ id: string; title: string; level: number }> = [];
  const idCounts = new Map<string, number>();
  let match;

  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[1]!.length;
    const title = match[2]!.trim();
    // Match the ID generation logic in TutorialContent headingToId
    const baseId = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    const next = (idCounts.get(baseId) ?? 0) + 1;
    idCounts.set(baseId, next);
    const id = next === 1 ? baseId : `${baseId}-${next}`;

    steps.push({ id, title, level });
  }

  return steps;
}

/**
 * Extract workflow steps from tutorial content
 * Supports:
 * - "The workflow will:" + numbered steps
 * - Flow diagrams like "Step 1: ..." with branches "If approved: Step 3 ..."
 */
export function extractWorkflowFromContent(content: string): any | null {
  const raw = (content || "").trim();
  if (!raw) return null;

  // 1) Try "The workflow will:" + numbered list
  {
    const willMatch = raw.match(
      /(?:^|\n)\s*(?:The workflow will|Workflow steps?|Steps?)\s*:\s*\n/i,
    );
    const searchContent = willMatch
      ? raw.slice((willMatch.index || 0) + willMatch[0].length)
      : raw;

    const stepRegex =
      /^\s*\d+\.\s+(?:\*\*)?(.+?)(?:\*\*)?\s*(?:[-–—:]\s*(.*))?\s*$/gm;
    const steps: Array<{ id: string; name: string; description: string }> = [];
    let m: RegExpExecArray | null;
    while ((m = stepRegex.exec(searchContent)) !== null) {
      const name = (m[1] || "").trim().replace(/\*\*/g, "");
      const desc = (m[2] || "" || name).trim().replace(/\*\*/g, "");
      if (!name) continue;
      const id = `step_${name.toLowerCase().replace(/[^a-z0-9]+/g, "_")}_${steps.length}`;
      steps.push({ id, name, description: desc || name });
    }

    if (steps.length > 0) {
      return {
        name: "Tutorial Workflow",
        description: "Workflow steps from tutorial example",
        cron_schedule: null,
        envs: [],
        pip_packages: [],
        inputs: [],
        start_at: [steps[0]!.id],
        steps: steps.map((s, idx) => ({
          id: s.id,
          name: s.name,
          description: s.description,
          agent_prompt: null,
          services: [],
          is_human_input_step: null,
          human_input_prompt: null,
          depends_on: idx > 0 ? [steps[idx - 1]!.id] : [],
          next_steps:
            idx < steps.length - 1
              ? [{ step_id: steps[idx + 1]!.id, condition: null }]
              : null,
        })),
      };
    }
  }

  // 2) Try "Step X:" flow diagram with branches
  {
    const lines = raw.split("\n");
    const stepLineRegex = /^\s*Step\s+(\d+)\s*:\s*(.+)\s*$/i;
    const stepsByNum = new Map<
      number,
      { id: string; name: string; description: string }
    >();
    const order: number[] = [];

    for (const line of lines) {
      const m = line.match(stepLineRegex);
      if (!m) continue;
      const n = Number(m[1]);
      if (!Number.isFinite(n)) continue;
      const title = (m[2] || "").trim();
      if (!stepsByNum.has(n)) {
        const id = `step_${n}`;
        stepsByNum.set(n, {
          id,
          name: title || `Step ${n}`,
          description: title || `Step ${n}`,
        });
        order.push(n);
      }
    }

    if (order.length > 0) {
      order.sort((a, b) => a - b);

      // For each step block, look for branch lines that reference another step number
      const nextByNum = new Map<
        number,
        Array<{ to: number; conditionName?: string | null }>
      >();
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]!;
        const m = line.match(stepLineRegex);
        if (!m) continue;
        const fromNum = Number(m[1]);
        // scan until next Step line
        let j = i + 1;
        while (j < lines.length && !lines[j]!.match(stepLineRegex)) {
          const l = lines[j]!;
          const condMatch = l.match(/If\s+([^:]+)\s*:\s*(.*)$/i);
          const refMatch = l.match(/Step\s+(\d+)/i);
          if (refMatch) {
            const toNum = Number(refMatch[1]);
            if (Number.isFinite(toNum)) {
              const conditionName = condMatch
                ? (condMatch[1] || "").trim()
                : null;
              const arr = nextByNum.get(fromNum) || [];
              arr.push({ to: toNum, conditionName: conditionName || null });
              nextByNum.set(fromNum, arr);
            }
          }
          j++;
        }
      }

      const stepsArr = order.map((n) => stepsByNum.get(n)!).filter(Boolean);

      return {
        name: "Tutorial Workflow",
        description: "Workflow steps from tutorial example",
        cron_schedule: null,
        envs: [],
        pip_packages: [],
        inputs: [],
        start_at: stepsArr.length > 0 ? [stepsArr[0]!.id] : [],
        steps: stepsArr.map((s, idx) => {
          const fromNum = order[idx]!;
          const next = nextByNum.get(fromNum);
          if (next && next.length > 0) {
            return {
              id: s.id,
              name: s.name,
              description: s.description,
              agent_prompt: null,
              services: [],
              is_human_input_step: null,
              human_input_prompt: null,
              depends_on: idx > 0 ? [stepsArr[idx - 1]!.id] : [],
              next_steps: next
                .filter((x) => stepsByNum.has(x.to))
                .map((x) => ({
                  step_id: stepsByNum.get(x.to)!.id,
                  condition: x.conditionName
                    ? {
                        name: x.conditionName,
                        description: x.conditionName,
                        agent_prompt: "",
                      }
                    : null,
                })),
            };
          }
          // fallback sequential
          return {
            id: s.id,
            name: s.name,
            description: s.description,
            agent_prompt: null,
            services: [],
            is_human_input_step: null,
            human_input_prompt: null,
            depends_on: idx > 0 ? [stepsArr[idx - 1]!.id] : [],
            next_steps:
              idx < stepsArr.length - 1
                ? [{ step_id: stepsArr[idx + 1]!.id, condition: null }]
                : null,
          };
        }),
      };
    }
  }

  return null;
}
